import React from 'react';
import { Menu, MenuItem, Button, Typography } from '@mui/material';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';

/**A button with a menu popover created from Menu, MenuItem, Button and Typography MUI components.
 *
 * @param {string} label A string value to display as the menu option
 * @param {function} actions A function that takes whatever relative actions/state changes are necessary for the button.
 * @param {boolean} disabled A boolean value that controls whether the base Button is interactive or not. Used typically with doingAjax/isDoingAjax.
 * @param {string} variant A string value that defaults to 'outlined'. Any valid MUI variant for a Button.
 * @param {object} ButtonProps A MUI ButtonProps object for the Button component.
 * @param {object} MenuProps A MUI MenuProps object for the Menu component.
 * @param {object} anchorOrigin A MUI anchorOrigin object for the Menu component.
 * @param {object} transformOrigin A MUI transformOrigin object for the Menu component.
 * @see {@link https://mui.com/material-ui/api/button/ MUI Button API} for further information.
 * @see {@link https://mui.com/material-ui/api/menu/ MUI Menu API} for further information.
 * @see {@link https://mui.com/material-ui/api/menu-item/ MUI Menu Item API} for further information.
 * @see {@link https://mui.com/material-ui/api/Typography/ MUI Typography API} for further information.
 */
export default function ShipyardMenuButton({
    label,
    actions,
    disabled,
    variant = 'outlined',
    ButtonProps = {},
    MenuProps = {},
    anchorOrigin = { vertical: 'top', horizontal: 'right' },
    transformOrigin = { vertical: 'bottom', horizontal: 'right' },
}) {
    const [contrastColor] = React.useState(() => {
        switch (variant) {
            case 'outlined':
                return '#014E8D';
            default:
                return '#FFF';
        }
    });
    const [isOpen, setIsOpen] = React.useState(false);
    const anchorEl = React.useRef();

    const handleActionClick = (actionFunc) => {
        setIsOpen(false);
        actionFunc?.();
    };

    let arrowTransitionStyle = { transition: 'ease transform .3s', transform: `rotate(${isOpen ? '180deg' : '0'})`, fontSize: '20px' };

    return (
        <>
            <Button
                variant={variant}
                onClick={() => setIsOpen((prev) => !prev)}
                ref={anchorEl}
                sx={{
                    border: '2px solid #014E8D',
                    '&.MuiButton-root:hover': {
                        borderWidth: '2px',
                    },
                }}
                disabled={disabled}
                {...ButtonProps}
            >
                <Typography
                    sx={{
                        borderRight: (theme) => `2px solid ${contrastColor}${disabled ? '40' : ''}`,
                        paddingRight: '12px',
                        marginRight: '8px',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        minWidth: '5rem',
                    }}
                >
                    {label}
                </Typography>
                {anchorOrigin?.vertical === 'top' ? (
                    <MdKeyboardArrowDown style={arrowTransitionStyle} />
                ) : (
                    <MdKeyboardArrowUp style={arrowTransitionStyle} />
                )}
            </Button>
            <Menu
                id={`shipyard-menu-${label}`}
                elevation={2}
                open={isOpen}
                anchorEl={anchorEl.current}
                onClose={(event, reason) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') setIsOpen(false);
                }}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                disableScrollLock={true}
                sx={{
                    '& .MuiPaper-root': {
                        borderRadius: '8px',
                        padding: (theme) => theme.spacing(1),
                    },
                }}
                {...MenuProps}
            >
                {actions.map((action) => {
                    return action?.disabled ? null : (
                        <MenuItem
                            onClick={() => handleActionClick(action.action)}
                            key={action?.key || `menu-item-${label}`}
                            disabled={action?.readOnly}
                            sx={theme => {
                                return {
                                    color: theme.palette?.[action?.type]?.main,
                                    '&:hover': {
                                        backgroundColor: theme.palette?.[action?.type]?.backgroundColor,
                                    }
                                }
                            }}
                        >
                            <Typography>{action.label}</Typography>
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
}
